import React from "react";
import Preloader from "../helper/Preloader";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import HeaderTwo from "../components/HeaderTwo";
import FooterTwo from "../components/FooterTwo";
import BottomFooter from "../components/BottomFooter";
import ScrollToTop from "react-scroll-to-top";
import ColorInit from "../helper/ColorInit";
import * as Yup from "yup";
import TextInput from "../components/TextInput";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { handleError } from "../utils/functions";
import SubmitButton from "../components/SubmitButton";
import { toast } from "react-toastify";
import axiosInstance from "../config/axios.config";
import { GoogleLogin, GoogleOAuthProvider } from "@react-oauth/google";
import { jwtDecode } from "jwt-decode";
import { useDispatch } from "react-redux";
import { setAuth } from "../store/slice/auth";

const schema = Yup.object().shape({
  name: Yup.string().required("Name is required"),
  email: Yup.string().email("Invalid email format").required("Email is required"),
  phoneNumber: Yup.string()
    .matches(/^[0-9]+$/, "Phone number must contain only digits")
    .min(10, "Phone number must be at least 10 digits")
    .required("Phone number is required"),
  password: Yup.string()
    .required("Password is Required")
    .matches(/^(?=.*[a-z])/, "Must Contain One Lowercase Character")
    .matches(/^(?=.*[A-Z])/, "Must Contain One Uppercase Character")
    .matches(/^(?=.*[0-9])/, "Must Contain One Number Character")
    .matches(/^(?=.*[!@#$%^&*])/, "Must Contain One Special Case Character")
    .min(8, "Password must be at least 8 characters long"),
});

const AccountPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    formState: { isSubmitting, errors },
    setValue,
  } = useForm({ resolver: yupResolver(schema), mode: "all" });

  const onSubmit = async (data) => {
    try {
      const { data: res } = await axiosInstance.post("/user/signup", data);
      if (!res.error) {
        navigate(`/user-verify?token=${res.token}`);
      } else {
        toast.error(res.message);
      }
    } catch (error) {
      handleError(error);
    }
  };

  const loginWithGoogle = async (credential) => {
    try {
      const body = {
        email: credential.email,
        name: credential.name,
        profile: credential.picture,
      };
      const { data } = await axiosInstance.post("/user/google", body);
      if (!data.error) {
        dispatch(setAuth({ user: {}, token: data.token, userType: 1 }));
        navigate("/");
      } else {
        toast.error(data.error);
      }
    } catch (error) {
      handleError(error);
    }
  };

  return (
    <>
      {/* ColorInit */}
      <ColorInit color={true} />

      {/* ScrollToTop */}
      <ScrollToTop smooth color="#FA6400" />

      {/* Preloader */}
      <Preloader />

      {/* HeaderTwo */}
      <HeaderTwo category={true} />

      <section className="account py-80">
        <div className="container">
          <div className="row justify-content-around">
            <div className="col-md-6">
              <div className="card">
                <form onSubmit={handleSubmit(onSubmit)} className="card-body py-30 px-30">
                  <h3 className="my-30 text-center">{t("userSignUp")}</h3>
                  <div className="row">
                    <div>
                      <TextInput
                        name={"name"}
                        placeholder={t("enterFullName")}
                        isRequired
                        className="mb-10"
                        label={t("fullName")}
                        type={"text"}
                        register={register}
                        onChange={(e) => setValue("name", e.target.value)}
                        error={errors.name}
                      />
                    </div>
                    <div className="col-md-6">
                      <div className="mb-24">
                        <TextInput
                          name={"email"}
                          placeholder={t("enterEmail")}
                          isRequired
                          label={t("email")}
                          type={"email"}
                          register={register}
                          onChange={(e) => setValue("email", e.target.value)}
                          error={errors.email}
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-24">
                        <TextInput
                          name={"phoneNumber"}
                          placeholder={t("enterPhone")}
                          isRequired
                          label={t("phone")}
                          type={"text"}
                          register={register}
                          onChange={(e) => setValue("phoneNumber", e.target.value)}
                          error={errors.phoneNumber}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="mb-24">
                    <TextInput
                      name={"password"}
                      placeholder={t("enterPassword")}
                      isRequired
                      label={t("password.password")}
                      type={"password"}
                      hasicon
                      register={register}
                      error={errors.password}
                      onChange={(e) => setValue("password", e.target.value)}
                    />
                  </div>

                  <div className="mt-30 text-center">
                    <SubmitButton isSubmitting={isSubmitting}>{t("signup")}</SubmitButton>
                    <p className="mt-20">
                      {t("alreadyHaveAccount")}{" "}
                      <Link to="/user-login" className="text-main-600 text-decoration-underline">
                        {t("login")}
                      </Link>
                    </p>
                  </div>
                </form>
                <div className="d-flex mb-20 justify-content-center">
                  <GoogleOAuthProvider clientId="667919689139-spqe0qhtll4cbir08g085qd28as794oc.apps.googleusercontent.com">
                    <GoogleLogin
                      onSuccess={(credentialResponse) => {
                        if (credentialResponse.credential != null) {
                          const USER_CREDENTIAL = jwtDecode(credentialResponse.credential);
                          loginWithGoogle(USER_CREDENTIAL);
                        }
                      }}
                      onError={(error) => {
                        console.error(error);
                      }}
                      shape="circle"
                      theme="outline"
                      width="100%"
                      type="standard"
                      text="Google Login"
                      logo_alignment="center"
                    />
                  </GoogleOAuthProvider>
                </div>
              </div>
            </div>
            <div className="col-md-5">
              <img src="/assets/images/userR.jpg" className="w-100 rounded-11" alt={t("registerImage")} />
            </div>
          </div>
        </div>
      </section>

      {/* FooterTwo */}
      <FooterTwo />

      {/* BottomFooter */}
      <BottomFooter />
    </>
  );
};

export default AccountPage;
